import { useEffect, useRef, useState } from 'react';
import { normalizeModelKey, type ModelProfile, type Settings } from '@voicer/shared';
import { useStore } from '../state/store';
import { ws } from '../ws/client';
import { exportBackup, importBackup } from './backup';

type NumKey = { [K in keyof Settings]: Settings[K] extends number ? K : never }[keyof Settings];

interface NumField {
  key: NumKey;
  label: string;
  hint: string;
  min: number;
  max: number;
  step: number;
}

const GROUPS: { title: string; fields: NumField[] }[] = [
  {
    title: 'Voice activity and endpointing',
    fields: [
      { key: 'vadPositiveThreshold', label: 'VAD positive threshold', hint: 'Silero probability to start speech', min: 0.2, max: 0.95, step: 0.01 },
      { key: 'vadNegativeThreshold', label: 'VAD negative threshold', hint: 'Probability below which speech may end', min: 0.05, max: 0.9, step: 0.01 },
      { key: 'vadSilenceMs', label: 'Silence to end segment (ms)', hint: 'Lower = faster turn, more cut-offs (default 350)', min: 150, max: 1500, step: 10 },
      { key: 'vadMinSpeechMs', label: 'Minimum speech (ms)', hint: 'Shorter segments are ignored as misfires', min: 40, max: 600, step: 10 },
      { key: 'vadPreSpeechPadMs', label: 'Pre-speech padding (ms)', hint: 'Audio kept before detected onset (800+ ms recommended; short values clip word starts)', min: 0, max: 1500, step: 10 },
      { key: 'eotExtendMs', label: 'Incomplete-turn extension (ms)', hint: 'Extra wait only when the last words look unfinished (and, because, …)', min: 200, max: 4000, step: 50 },
      {
        key: 'commitGraceHighMs',
        label: 'Commit grace high (ms)',
        hint: 'Silence after a high-confidence complete reply (yes/okay/terminal punct; default 180)',
        min: 0,
        max: 2000,
        step: 20,
      },
      { key: 'commitGraceMs', label: 'Commit grace (ms)', hint: 'Silence after a medium-confidence complete utterance (default 350)', min: 0, max: 3000, step: 50 },
      { key: 'continuationRecallMs', label: 'Continuation recall window (ms)', hint: 'Speaking again this soon after a commit (before any reply audio) pulls the turn back and merges', min: 0, max: 4000, step: 50 },
    ],
  },
  {
    title: 'Barge-in arbiter',
    fields: [
      { key: 'duckLevel', label: 'Duck level', hint: 'Playback gain while evaluating an interjection', min: 0, max: 1, step: 0.05 },
      { key: 'overlapHardMs', label: 'Overlap hard interrupt (ms)', hint: 'Fallback: speech longer than this always interrupts (pause normally fires first)', min: 400, max: 5000, step: 50 },
      { key: 'bargeinMinFrames', label: 'Onset confirmation frames', hint: 'VAD frames (32 ms) needed before ducking', min: 1, max: 10, step: 1 },
      { key: 'bargeinPauseAfterMs', label: 'Pause after sustained speech (ms)', hint: 'Playback pauses once speech has lasted this long and looks like talking', min: 150, max: 1500, step: 25 },
      { key: 'bargeinSpeechRatio', label: 'Pause speech ratio', hint: 'Fraction of frames since onset that must be speech-positive', min: 0.2, max: 1, step: 0.05 },
      { key: 'bargeinMinSyllables', label: 'Pause min syllables', hint: 'Energy peaks required; a single cough or clap has one', min: 1, max: 6, step: 1 },
      { key: 'bargeinMinSegmentMs', label: 'Min segment for STT (ms)', hint: 'Shorter interjections are dismissed as noise without transcription', min: 0, max: 800, step: 25 },
      { key: 'echoCorrelationThreshold', label: 'Echo correlation threshold', hint: 'Mic/playback envelope correlation treated as self-speech', min: 0.2, max: 0.95, step: 0.05 },
      { key: 'tierBMaxWords', label: 'Tier B max words', hint: 'Longer interjections skip the in-context classifier and interrupt', min: 2, max: 20, step: 1 },
    ],
  },
  {
    title: 'Speech synthesis pipeline',
    fields: [
      {
        key: 'prefetchDepth',
        label: 'TTS prefetch depth',
        hint: 'Unplayed chunks to buffer before pausing generation. Slow TTS: 3–4; fast Kokoro can use 2',
        min: 1,
        max: 6,
        step: 1,
      },
      {
        key: 'firstChunkMinWords',
        label: 'First chunk min words',
        hint: 'Earliest clause/newline cut for the first speakable chunk (lower = faster first audio)',
        min: 3,
        max: 20,
        step: 1,
      },
      {
        key: 'firstChunkMaxWords',
        label: 'First chunk max words',
        hint: 'Hard cut for the first chunk when there is no punctuation',
        min: 6,
        max: 40,
        step: 1,
      },
    ],
  },
  {
    title: 'Generation',
    fields: [
      { key: 'maxGenTokens', label: 'Max reply tokens', hint: 'Hard cap; long case answers need 1024–2048+', min: 32, max: 8192, step: 64 },
      { key: 'temperature', label: 'Temperature', hint: '', min: 0, max: 2, step: 0.05 },
      { key: 'topP', label: 'Top-p', hint: '', min: 0.1, max: 1, step: 0.01 },
      { key: 'topK', label: 'Top-k', hint: '', min: 0, max: 200, step: 1 },
      { key: 'minP', label: 'Min-p', hint: '', min: 0, max: 0.5, step: 0.01 },
      { key: 'repPen', label: 'Repetition penalty', hint: '', min: 1, max: 1.5, step: 0.01 },
    ],
  },
  {
    title: 'Context budget',
    fields: [
      { key: 'genReserveTokens', label: 'Generation reserve (tokens)', hint: 'Kept free for the reply', min: 64, max: 2048, step: 16 },
      { key: 'budgetMemories', label: 'Memories share', hint: 'Fraction of the remaining budget', min: 0, max: 0.5, step: 0.01 },
      { key: 'budgetSummary', label: 'Summary share', hint: '', min: 0, max: 0.5, step: 0.01 },
      { key: 'budgetRetrieved', label: 'Retrieved share', hint: '', min: 0, max: 0.5, step: 0.01 },
      { key: 'retrievalTopK', label: 'Retrieval top-k', hint: '', min: 0, max: 20, step: 1 },
      { key: 'backgroundIdleMs', label: 'Background idle delay (ms)', hint: 'Idle time before summaries/memory extraction run', min: 500, max: 15000, step: 250 },
      { key: 'statusPollMs', label: 'Status poll interval (ms)', hint: '', min: 500, max: 10000, step: 250 },
    ],
  },
];

/** Sensible starter stops for RP / multi-speaker models that invent user lines. */
const RP_STOP_HINT = ['User:', 'You:', '\nUser:', '\nYou:', '<|im_start|>user', '<|user|>'];

export function SettingsPanel() {
  const settings = useStore((s) => s.settings);
  const profiles = useStore((s) => s.profiles);
  const status = useStore((s) => s.status);
  const [local, setLocal] = useState<Settings>(settings);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => setLocal(settings), [settings]);

  const modelKey = normalizeModelKey(status?.model);
  const activeProfile = profiles.find((p) => p.key === modelKey) ?? null;

  const push = (patch: Partial<Settings>) => {
    setLocal((l) => ({ ...l, ...patch }));
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void ws.rpc('settings.update', { patch }).catch(() => {}), 250);
  };
  const pushNow = (patch: Partial<Settings>) => {
    setLocal((l) => ({ ...l, ...patch }));
    void ws.rpc('settings.update', { patch }).catch(() => {});
  };

  const saveProfile = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const p = await ws.rpc<ModelProfile>('profiles.save', {});
      setMsg(`Saved profile for ${p.label}`);
    } catch (err) {
      setMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="settings">
      <div className="panel">
        <h3>Conversation storage</h3>
        <p className="dim" style={{ margin: '0 0 10px', fontSize: 12 }}>
          Conversations are stored in Supabase by default. Turn this off to keep chat in RAM only until the proxy
          restarts (settings and model profiles still save to Supabase). Clear removes all conversations, memories,
          metrics, and caches from the database without changing settings or profiles.
        </p>
        <div className="field">
          <label>Save conversations to Supabase</label>
          <div className="val">
            <input
              type="checkbox"
              checked={local.sqlPersistenceEnabled}
              disabled={busy}
              onChange={(e) => {
                const next = e.target.checked;
                if (
                  !next &&
                  !confirm(
                    'Conversations will no longer be saved to Supabase. Existing chat data will be cleared from the database. Continue?',
                  )
                ) {
                  return;
                }
                pushNow({ sqlPersistenceEnabled: next });
              }}
            />
          </div>
        </div>
        <div className="row">
          <button
            className="small danger"
            disabled={busy}
            onClick={() => {
              if (
                !confirm(
                  'Clear all conversations, messages, memories, metrics, and caches from Supabase? Settings and model profiles are kept.',
                )
              ) {
                return;
              }
              setBusy(true);
              void ws
                .rpc('storage.clear')
                .then(() => setMsg('Cleared chat data'))
                .catch((err) => setMsg(`Clear failed: ${err instanceof Error ? err.message : err}`))
                .finally(() => setBusy(false));
            }}
          >
            Clear chat data
          </button>
          {msg && <span className="dim">{msg}</span>}
        </div>
      </div>

      <div className="panel">
        <h3>Backup</h3>
        <p className="dim" style={{ margin: '0 0 10px', fontSize: 12 }}>
          Export the current conversation, all tuning settings, and model profiles as one JSON file. Import creates a new chat named “… (imported)” and restores settings from the file.
        </p>
        <div className="row">
          <button
            className="small"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void exportBackup()
                .then(() => setMsg('Exported backup'))
                .catch((err) => setMsg(`Export failed: ${err instanceof Error ? err.message : err}`))
                .finally(() => setBusy(false));
            }}
          >
            Export
          </button>
          <button
            className="small"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void importBackup('new')
                .then(() => setMsg('Imported backup'))
                .catch((err) => setMsg(`Import failed: ${err instanceof Error ? err.message : err}`))
                .finally(() => setBusy(false));
            }}
          >
            Import
          </button>
          {msg && <span className="dim">{msg}</span>}
        </div>
      </div>

      <div className="panel">
        <h3>Model providers</h3>
        <p className="dim" style={{ margin: '0 0 10px', fontSize: 12 }}>
          Choose KoboldCPP or Google Vertex AI independently for LLM, speech-to-text, and text-to-speech. Vertex setup:{' '}
          <code>Documentation/VERTEX_SETUP.md</code>
        </p>
        {(
          [
            ['llmProvider', 'LLM'],
            ['sttProvider', 'STT'],
            ['ttsProvider', 'TTS'],
          ] as const
        ).map(([key, label]) => (
          <div className="field" key={key}>
            <label>{label}</label>
            <div className="val">
              <select
                value={local[key]}
                onChange={(e) => pushNow({ [key]: e.target.value as Settings['llmProvider'] })}
              >
                <option value="kobold">KoboldCPP</option>
                <option value="vertex">Google Vertex AI</option>
              </select>
            </div>
          </div>
        ))}

        {(local.llmProvider === 'kobold' || local.sttProvider === 'kobold' || local.ttsProvider === 'kobold') && (
          <>
            <h4 style={{ margin: '14px 0 8px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.7 }}>KoboldCPP</h4>
            <div className="field">
              <label>URL</label>
              <div className="val">
                <input type="text" value={local.koboldUrl} onChange={(e) => setLocal({ ...local, koboldUrl: e.target.value })} onBlur={() => pushNow({ koboldUrl: local.koboldUrl })} style={{ width: 140 }} />
              </div>
            </div>
            <div className="field">
              <label>
                STT URL <small>optional separate Whisper endpoint (empty = same as URL)</small>
              </label>
              <div className="val">
                <input type="text" value={local.sttUrl ?? ''} onChange={(e) => setLocal({ ...local, sttUrl: e.target.value })} onBlur={() => pushNow({ sttUrl: local.sttUrl })} style={{ width: 140 }} placeholder="http://localhost:5002" />
              </div>
            </div>
            <div className="field">
              <label>
                TTS URL <small>optional separate TTS endpoint (empty = same as URL)</small>
              </label>
              <div className="val">
                <input type="text" value={local.ttsUrl ?? ''} onChange={(e) => setLocal({ ...local, ttsUrl: e.target.value })} onBlur={() => pushNow({ ttsUrl: local.ttsUrl })} style={{ width: 140 }} placeholder="http://localhost:5003" />
              </div>
            </div>
            <div className="field">
              <label>
                Password <small>only if launched with --password</small>
              </label>
              <div className="val">
                <input type="password" value={local.koboldPassword} onChange={(e) => setLocal({ ...local, koboldPassword: e.target.value })} onBlur={() => pushNow({ koboldPassword: local.koboldPassword })} style={{ width: 140 }} />
              </div>
            </div>
          </>
        )}

        {(local.llmProvider === 'vertex' || local.sttProvider === 'vertex' || local.ttsProvider === 'vertex') && (
          <>
            <h4 style={{ margin: '14px 0 8px', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.7 }}>Google Vertex AI</h4>
            <div className="field">
              <label>Project ID</label>
              <div className="val">
                <input
                  type="text"
                  value={local.vertexProjectId}
                  onChange={(e) => setLocal({ ...local, vertexProjectId: e.target.value })}
                  onBlur={() => pushNow({ vertexProjectId: local.vertexProjectId })}
                  style={{ width: 160 }}
                  placeholder="my-gcp-project"
                />
              </div>
            </div>
            <div className="field">
              <label>
                Location <small>default us-central1</small>
              </label>
              <div className="val">
                <input
                  type="text"
                  value={local.vertexLocation}
                  onChange={(e) => setLocal({ ...local, vertexLocation: e.target.value })}
                  onBlur={() => pushNow({ vertexLocation: local.vertexLocation })}
                  style={{ width: 140 }}
                />
              </div>
            </div>
            <div className="field">
              <label>
                Credentials JSON path{' '}
                <small>SA key or WIF external_account config; empty = ADC / GOOGLE_APPLICATION_CREDENTIALS</small>
              </label>
              <div className="val">
                <input
                  type="text"
                  value={local.vertexCredentialsPath}
                  onChange={(e) => setLocal({ ...local, vertexCredentialsPath: e.target.value })}
                  onBlur={() => pushNow({ vertexCredentialsPath: local.vertexCredentialsPath })}
                  style={{ width: 200 }}
                  placeholder="C:\secrets\sa.json"
                />
              </div>
            </div>
            <div className="field">
              <label>
                Impersonate service account <small>optional SA email; keyless local auth</small>
              </label>
              <div className="val">
                <input
                  type="text"
                  value={local.vertexImpersonateServiceAccount ?? ''}
                  onChange={(e) => setLocal({ ...local, vertexImpersonateServiceAccount: e.target.value })}
                  onBlur={() =>
                    pushNow({ vertexImpersonateServiceAccount: local.vertexImpersonateServiceAccount })
                  }
                  style={{ width: 220 }}
                  placeholder="voicer@PROJECT.iam.gserviceaccount.com"
                />
              </div>
            </div>
            <div className="field">
              <label>
                LLM model{' '}
                <small>flash-lite = low latency (default); flash/pro = higher quality, slower TTFT</small>
              </label>
              <div className="val">
                <input
                  type="text"
                  value={local.vertexLlmModel}
                  onChange={(e) => setLocal({ ...local, vertexLlmModel: e.target.value })}
                  onBlur={() => pushNow({ vertexLlmModel: local.vertexLlmModel })}
                  style={{ width: 220 }}
                  placeholder="gemini-2.5-flash-lite"
                />
              </div>
            </div>
            <div className="field">
              <label>
                Compile max tokens <small>soft cap for Vertex prompt size (default 32768)</small>
              </label>
              <div className="val">
                <input
                  type="number"
                  min={4096}
                  max={1048576}
                  step={1024}
                  value={local.vertexCompileMaxTokens}
                  onChange={(e) => setLocal({ ...local, vertexCompileMaxTokens: Number(e.target.value) })}
                  onBlur={() => pushNow({ vertexCompileMaxTokens: local.vertexCompileMaxTokens })}
                  style={{ width: 120 }}
                />
              </div>
            </div>
            <div className="field">
              <label>
                STT model <small>chirp_3</small>
              </label>
              <div className="val">
                <input
                  type="text"
                  value={local.vertexSttModel}
                  onChange={(e) => setLocal({ ...local, vertexSttModel: e.target.value })}
                  onBlur={() => pushNow({ vertexSttModel: local.vertexSttModel })}
                  style={{ width: 160 }}
                />
              </div>
            </div>
            <div className="field">
              <label>TTS model</label>
              <div className="val">
                <input
                  type="text"
                  value={local.vertexTtsModel}
                  onChange={(e) => setLocal({ ...local, vertexTtsModel: e.target.value })}
                  onBlur={() => pushNow({ vertexTtsModel: local.vertexTtsModel })}
                  style={{ width: 220 }}
                />
              </div>
            </div>
            <div className="field">
              <label>TTS voice</label>
              <div className="val">
                <input
                  type="text"
                  value={local.vertexTtsVoice}
                  onChange={(e) => setLocal({ ...local, vertexTtsVoice: e.target.value })}
                  onBlur={() => pushNow({ vertexTtsVoice: local.vertexTtsVoice })}
                  style={{ width: 160 }}
                />
              </div>
            </div>
            <div className="row" style={{ marginTop: 8 }}>
              <button
                className="small"
                disabled={busy}
                onClick={() => {
                  setBusy(true);
                  void ws
                    .rpc<{ ok: boolean; error?: string; checks?: { name: string; ok: boolean; detail?: string }[] }>('vertex.test', {})
                    .then((r) => {
                      if (r.ok) setMsg('Vertex connection OK');
                      else {
                        const failed = r.checks?.filter((c) => !c.ok).map((c) => `${c.name}: ${c.detail}`).join('; ');
                        setMsg(`Vertex test failed: ${failed || r.error || 'unknown'}`);
                      }
                    })
                    .catch((err) => setMsg(`Vertex test failed: ${err instanceof Error ? err.message : err}`))
                    .finally(() => setBusy(false));
                }}
              >
                Test Vertex connection
              </button>
              {msg && <span className="dim">{msg}</span>}
            </div>
          </>
        )}

        <div className="field" style={{ marginTop: 12 }}>
          <label>
            Streaming STT <small>mid-utterance partials for earlier prepare (extra STT load)</small>
          </label>
          <div className="val">
            <input type="checkbox" checked={!!local.streamingStt} onChange={(e) => pushNow({ streamingStt: e.target.checked })} />
          </div>
        </div>
        <div className="field">
          <label>
            Instruct template <small>auto guesses from the model name ({status?.template ?? '?'}); ignored for Vertex Gemini</small>
          </label>
          <div className="val">
            <select value={local.instructTemplate} onChange={(e) => pushNow({ instructTemplate: e.target.value as Settings['instructTemplate'] })}>
              {['auto', 'chatml', 'llama3', 'gemma', 'mistral', 'phi', 'alpaca'].map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="field">
          <label>
            Generation mode{' '}
            <small>
              sentence = speak the first sentence ASAP, then generate the next while it plays (best for voice). full = wait for the whole reply
              before any TTS (cleanest for some RP models)
            </small>
          </label>
          <div className="val">
            <select value={local.interleaveMode} onChange={(e) => pushNow({ interleaveMode: e.target.value as Settings['interleaveMode'] })}>
              <option value="sentence">sentence-interleaved (faster TTS)</option>
              <option value="full">full reply, then TTS</option>
            </select>
          </div>
        </div>
      </div>

      <div className="panel">
        <h3>
          Model profile
          <span className="spacer" />
          <span className="dim" style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
            {status?.model?.replace(/^koboldcpp\//i, '') ?? 'no model detected'}
          </span>
        </h3>
        <p className="dim" style={{ margin: '0 0 10px', fontSize: 12 }}>
          Save sampler/template settings per GGUF. When KoboldCPP loads a model that has a saved profile, LegalAI: Voice applies it automatically.
        </p>
        <div className="field">
          <label>Auto-apply profile when the model changes</label>
          <div className="val">
            <input type="checkbox" checked={local.autoApplyModelProfile !== false} onChange={(e) => pushNow({ autoApplyModelProfile: e.target.checked })} />
          </div>
        </div>
        <div className="row" style={{ marginBottom: 8 }}>
          <button className="primary small" disabled={busy || !modelKey} onClick={() => void saveProfile()}>
            {activeProfile ? 'Update profile for current model' : 'Save profile for current model'}
          </button>
          {activeProfile && (
            <button
              className="small danger"
              disabled={busy}
              onClick={() => void ws.rpc('profiles.delete', { key: activeProfile.key }).then(() => setMsg(`Deleted ${activeProfile.label}`))}
            >
              delete current
            </button>
          )}
        </div>
        {msg && (
          <div className="dim" style={{ fontSize: 12, marginBottom: 8 }}>
            {msg}
          </div>
        )}
        {profiles.length > 0 && (
          <table className="data">
            <thead>
              <tr>
                <th>model</th>
                <th>template</th>
                <th>mode</th>
                <th className="num">temp</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((p) => (
                <tr key={p.key} style={p.key === modelKey ? { background: 'rgba(90,169,255,0.08)' } : undefined}>
                  <td title={p.key}>{p.label}</td>
                  <td>{p.instructTemplate}</td>
                  <td>{p.interleaveMode}</td>
                  <td className="num">{p.temperature}</td>
                  <td>
                    <button className="small" onClick={() => void ws.rpc('profiles.apply', { key: p.key }).then(() => setMsg(`Applied ${p.label}`))}>
                      apply
                    </button>{' '}
                    <button className="small danger" onClick={() => void ws.rpc('profiles.delete', { key: p.key })}>
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="panel">
        <h3>Voice</h3>
        <div className="field">
          <label>Text-to-speech</label>
          <div className="val">
            <input type="checkbox" checked={local.ttsEnabled} onChange={(e) => pushNow({ ttsEnabled: e.target.checked })} />
          </div>
        </div>
        <div className="field">
          <label>
            Speak quoted text only <small>TTS skips narrative; only dialogue inside "quotes" is spoken</small>
          </label>
          <div className="val">
            <input
              type="checkbox"
              checked={local.ttsQuotesOnly}
              disabled={!local.ttsEnabled}
              onChange={(e) => pushNow({ ttsQuotesOnly: e.target.checked })}
            />
          </div>
        </div>
        <div className="field">
          <label>
            Voice{' '}
            <small>
              {local.ttsProvider === 'vertex'
                ? status?.voices.length
                  ? `${status.voices.length} Vertex voices`
                  : 'Gemini / Chirp 3 HD name'
                : status?.voices.length
                  ? `${status.voices.length} available`
                  : 'type any name; KoboldCPP hashes unknown names into a seed'}
            </small>
          </label>
          <div className="val">
            {local.ttsProvider === 'vertex' ? (
              status?.voices.length ? (
                <select value={local.vertexTtsVoice} onChange={(e) => pushNow({ vertexTtsVoice: e.target.value, voice: e.target.value })}>
                  {!status.voices.includes(local.vertexTtsVoice) && <option value={local.vertexTtsVoice}>{local.vertexTtsVoice}</option>}
                  {status.voices.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={local.vertexTtsVoice}
                  onChange={(e) => setLocal({ ...local, vertexTtsVoice: e.target.value })}
                  onBlur={() => pushNow({ vertexTtsVoice: local.vertexTtsVoice, voice: local.vertexTtsVoice })}
                  style={{ width: 160 }}
                />
              )
            ) : status?.voices.length ? (
              <select value={local.voice} onChange={(e) => pushNow({ voice: e.target.value })}>
                {!status.voices.includes(local.voice) && <option value={local.voice}>{local.voice}</option>}
                {status.voices.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            ) : (
              <input type="text" value={local.voice} onChange={(e) => setLocal({ ...local, voice: e.target.value })} onBlur={() => pushNow({ voice: local.voice })} style={{ width: 140 }} />
            )}
          </div>
        </div>
        <div className="field">
          <label>
            Language <small>Whisper langcode and TTS language (en, de, ja, auto…)</small>
          </label>
          <div className="val">
            <input type="text" value={local.ttsLanguage} onChange={(e) => setLocal({ ...local, ttsLanguage: e.target.value })} onBlur={() => pushNow({ ttsLanguage: local.ttsLanguage })} style={{ width: 80 }} />
          </div>
        </div>
        <div className="field">
          <label>
            Semantic end-of-turn check <small>in-context COMPLETE/INCOMPLETE when the utterance looks unfinished</small>
          </label>
          <div className="val">
            <input type="checkbox" checked={local.eotSemanticCheck} onChange={(e) => pushNow({ eotSemanticCheck: e.target.checked })} />
          </div>
        </div>
        <div className="field">
          <label>
            Tier B in-context classifier <small>one-token grammar-constrained check for unclear interjections</small>
          </label>
          <div className="val">
            <input type="checkbox" checked={local.tierBEnabled} onChange={(e) => pushNow({ tierBEnabled: e.target.checked })} />
          </div>
        </div>
        <div className="field">
          <label>
            Memory extraction <small>background job after each turn (shares the KV prefix)</small>
          </label>
          <div className="val">
            <input type="checkbox" checked={local.memoryExtraction} onChange={(e) => pushNow({ memoryExtraction: e.target.checked })} />
          </div>
        </div>
      </div>

      <div className="panel">
        <h3>
          Extra stop sequences
          <span className="spacer" />
          <button
            className="small"
            onClick={() => {
              const merged = [...new Set([...(local.extraStops ?? []), ...RP_STOP_HINT])];
              pushNow({ extraStops: merged });
            }}
          >
            add RP defaults
          </button>
        </h3>
        <p className="dim" style={{ margin: '0 0 8px', fontSize: 12 }}>
          One stop string per line. Merged with the instruct template stops. Roleplay models (Rocinante, etc.) often need these to avoid inventing the next user turn.
        </p>
        <textarea
          value={(local.extraStops ?? []).join('\n')}
          onChange={(e) => setLocal({ ...local, extraStops: e.target.value.split('\n') })}
          onBlur={() =>
            pushNow({
              extraStops: (local.extraStops ?? []).map((s) => s.replace(/\r$/, '')).filter((s) => s.length > 0),
            })
          }
          placeholder={'User:\nYou:\n<|im_start|>user'}
          style={{ minHeight: 100, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 }}
        />
      </div>

      {GROUPS.map((g) => (
        <div className="panel" key={g.title}>
          <h3>{g.title}</h3>
          {g.fields.map((f) => (
            <div className="field" key={f.key}>
              <label>
                {f.label}
                {f.hint && <small>{f.hint}</small>}
              </label>
              <div className="val">
                <input type="range" min={f.min} max={f.max} step={f.step} value={local[f.key]} onChange={(e) => push({ [f.key]: Number(e.target.value) } as Partial<Settings>)} />
                <span>{formatNum(local[f.key], f.step)}</span>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function formatNum(v: number, step: number) {
  return step >= 1 ? String(Math.round(v)) : v.toFixed(step < 0.1 ? 2 : 1);
}
